import { useTranslations } from "@zapier/i18n";
import { HydratedAgent } from "@zapier/client";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Spacer,
  Text,
} from "@zapier/design-system";

import { FlexSpacer, useEmitAgentDeleteModalEvent } from "@zapier/shared";
import { useDeleteAgent } from "./useDeleteAgent";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  agent: HydratedAgent | undefined;
  folderId?: string;
}

export function AgentDeleteModal({ isOpen, onClose, agent, folderId }: Props) {
  const t = useTranslations("agent-delete-modal");

  const { deleteAgent, isPending } = useDeleteAgent(agent, folderId);
  const { emit } = useEmitAgentDeleteModalEvent();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      emit("cancel");
      onClose();
    }
  };

  if (!agent) {
    return;
  }

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={handleOpenChange}>
      <>
        <ModalDialogHeader>
          {t("header.delete-agent-title", {
            agentName: agent.name,
          })}
        </ModalDialogHeader>

        <ModalDialogBody>
          <Text color="TextWeaker">{t("body.permanent-deletion-warning")}</Text>
          <Spacer height={12} />
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            isDisabled={isPending}
            onPress={() => {
              emit("cancel");
              onClose();
            }}
            variant="tertiary"
          >
            {t("buttons.cancel")}
          </Button>

          <FlexSpacer />

          <Button
            isDisabled={isPending}
            isPending={isPending}
            onPress={() => {
              emit("confirm");
              deleteAgent();
              onClose();
            }}
            variant="danger"
          >
            {t("buttons.delete")}
          </Button>
        </ModalDialogFooter>
      </>
    </ModalDialogRoot>
  );
}

// For ease of use during `dynamic` import
export default AgentDeleteModal;
